import { all, call, put, race, select, take, takeLatest, takeLeading } from "redux-saga/effects";
import * as AuditLogService from "../../services/AuditLogService";
import { browserTimeZone, listParams } from "../../helpers/AuditLogHelper";
import { readErrorBody } from "../../helpers/ErrorMessage";
import { saveFile } from "../../helpers/SaveFile";
import {
  auditLogDetailsClose,
  auditLogDetailsFail,
  auditLogDetailsRequest,
  auditLogDetailsSuccess,
  auditLogExportFail,
  auditLogExportRequest,
  auditLogExportSuccess,
  auditLogListFail,
  auditLogListRequest,
  auditLogListSuccess,
  auditLogOptionsFail,
  auditLogOptionsRequest,
  auditLogOptionsSuccess,
} from "../slices/auditLogSlice";

const errorOf = (error) =>
  error?.response?.data || {
    status: error?.response?.status || 400,
    response: "SOMETHING_ELSE_WRONG",
  };

export const selectAuditLogQuery = (state) => state.auditLogs.query;

export function* loadList() {
  try {
    const query = yield select(selectAuditLogQuery);
    const ajax = yield call(AuditLogService.getAuditLogs, listParams(query));
    yield put(auditLogListSuccess(ajax.data));
  } catch (error) {
    yield put(auditLogListFail(errorOf(error)));
  }
}

export function* loadOptions() {
  try {
    const ajax = yield call(AuditLogService.getAuditLogOptions);
    yield put(auditLogOptionsSuccess(ajax.data));
  } catch (error) {
    yield put(auditLogOptionsFail(errorOf(error)));
  }
}

// A panel closed while its entry loads never receives the late answer.
export function* loadDetails({ payload }) {
  try {
    const { ajax } = yield race({
      ajax: call(AuditLogService.getAuditLog, payload),
      closed: take(auditLogDetailsClose.type),
    });
    if (ajax) {
      yield put(auditLogDetailsSuccess(ajax.data));
    }
  } catch (error) {
    yield put(auditLogDetailsFail(errorOf(error)));
  }
}

export function* exportLogs({ payload }) {
  try {
    const query = yield select(selectAuditLogQuery);
    const fileType = payload?.fileType === "csv" ? "csv" : "excel";
    // The browser's time zone: the API writes local times under "When (<zone>)".
    const ajax = yield call(AuditLogService.exportAuditLogs, fileType, {
      ...listParams(query),
      timezone: browserTimeZone(),
    });
    yield call(saveFile, ajax.data, `audit-logs.${fileType === "csv" ? "csv" : "xlsx"}`);
    yield put(auditLogExportSuccess());
  } catch (error) {
    // The file is asked for as a Blob, so a refusal's body arrives as one too
    // (as in UserSaga.exportUsers): it is read for the API's code.
    const body = yield call(readErrorBody, error?.response?.data);
    yield put(
      auditLogExportFail({
        response: "SOMETHING_ELSE_WRONG",
        ...body,
        status: error?.response?.status || body.status || 400,
      })
    );
  }
}

export default function* auditLogSaga() {
  yield all([
    takeLatest(auditLogListRequest.type, loadList),
    takeLatest(auditLogOptionsRequest.type, loadOptions),
    takeLatest(auditLogDetailsRequest.type, loadDetails),
    // One export at a time: a second click while one runs is ignored.
    takeLeading(auditLogExportRequest.type, exportLogs),
  ]);
}
